import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, Alert, MenuItem, CircularProgress, Card, Stack, Chip,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
  FormControlLabel, Switch,
} from '@mui/material';
import {
  ContentCopy as CopyIcon, Refresh as RefreshIcon, Add as AddIcon,
  LocalOffer as OfferIcon, Edit as EditIcon, AddBox as AddBoxIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { formatBRL } from '../../utils/formatBRL';

const API_URL = import.meta.env.VITE_SERVICE_URL_HOSTLY_API;
const API_KEY = import.meta.env.VITE_API_SECRET_KEY;
const APP_ID = '6755368891';
const headers = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };

interface Subscription { id: string; productId: string; name: string; }
interface CustomCode { id: string; customCode?: string; numberOfCodes?: number; active?: boolean; expirationDate?: string; }
interface Offer {
  id: string; name?: string; active?: boolean; duration?: string; offerMode?: string;
  eligibilities: string[]; totalNumberOfCodes?: number; customCodes: CustomCode[];
}
interface PriceTier { pricePointId: string; customerPrice: string; proceeds: string | null; }

const FRIENDLY_NAME: Record<string, string> = {
  '12': 'Basic Mensal', '1212': 'Basic Anual', '13': 'Pro Mensal', '1313': 'Pro Anual',
};
const ELIGIBILITY_LABEL: Record<string, string> = { NEW: 'Novos', EXISTING: 'Existentes', EXPIRED: 'Expirados' };
const redeemLink = (code: string) => `https://apps.apple.com/redeem?ctx=offercodes&id=${APP_ID}&code=${code}`;

export const AdminAppleOfferCodes: React.FC = () => {
  const { theme } = useTheme();
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [selectedSub, setSelectedSub] = useState('');
  const [currentPrice, setCurrentPrice] = useState<string | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const [newCodeByOffer, setNewCodeByOffer] = useState<Record<string, string>>({});
  const [limitByOffer, setLimitByOffer] = useState<Record<string, string>>({});
  const [creatingOffer, setCreatingOffer] = useState<string | null>(null);

  const [tiers, setTiers] = useState<PriceTier[]>([]);
  const [offerDialog, setOfferDialog] = useState(false);
  const [priceDialog, setPriceDialog] = useState(false);
  const [productDialog, setProductDialog] = useState(false);
  const [groups, setGroups] = useState<{ id: string; referenceName: string }[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const loadGroups = async () => {
    if (groups.length > 0) return;
    try {
      const res = await fetch(`${API_URL}/admin/appstore/groups`, { headers });
      const data = await res.json();
      if (res.ok) setGroups(data.items || []);
    } catch { /* silencioso */ }
  };

  const surface = { backgroundColor: theme.colors.background, borderRadius: theme.radii.large, boxShadow: theme.shadows.small };

  const checkStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/appstore/status`, { headers });
      const data = await res.json();
      setConfigured(!!data.configured);
      if (data.configured) await loadSubscriptions();
    } catch { setConfigured(false); }
  };

  const loadSubscriptions = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/appstore/subscriptions`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao carregar assinaturas');
      const subs: Subscription[] = data.subscriptions || [];
      setSubscriptions(subs);
      const first = subs.find(s => s.productId === '1313') || subs[0];
      if (first) { setSelectedSub(first.id); loadOverview(first.id); }
    } catch (e: any) { setMessage({ type: 'error', text: e.message }); }
  };

  const loadOverview = async (subscriptionId: string) => {
    setLoading(true); setOffers([]); setCurrentPrice(null); setMessage(null);
    try {
      const res = await fetch(`${API_URL}/admin/appstore/overview?subscriptionId=${subscriptionId}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao carregar ofertas');
      setCurrentPrice(data.currentPrice || null);
      setOffers(data.offers || []);
    } catch (e: any) { setMessage({ type: 'error', text: e.message }); }
    finally { setLoading(false); }
  };

  const loadTiers = async () => {
    if (tiers.length > 0 || !selectedSub) return;
    try {
      const res = await fetch(`${API_URL}/admin/appstore/price-tiers?subscriptionId=${selectedSub}`, { headers });
      const data = await res.json();
      if (res.ok) setTiers(data.tiers || []);
    } catch { /* silencioso */ }
  };

  const createCode = async (offerId: string) => {
    const code = (newCodeByOffer[offerId] || '').trim().toUpperCase();
    let limit = parseInt(limitByOffer[offerId] || '1000') || 1000;
    if (!code) { setMessage({ type: 'error', text: 'Informe o código' }); return; }
    // A Apple exige um mínimo de 500 resgates por código.
    if (limit < 500) {
      setMessage({ type: 'error', text: 'A Apple exige limite mínimo de 500 por código. Ajustado para 500.' });
      limit = 500;
      setLimitByOffer({ ...limitByOffer, [offerId]: '500' });
    }
    setCreatingOffer(offerId); setMessage(null);
    try {
      const res = await fetch(`${API_URL}/admin/appstore/custom-codes`, {
        method: 'POST', headers,
        body: JSON.stringify({ offerCodeId: offerId, customCode: code, numberOfCodes: limit }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao criar código');
      setMessage({ type: 'success', text: `Código ${code} criado.` });
      setNewCodeByOffer({ ...newCodeByOffer, [offerId]: '' });
      loadOverview(selectedSub);
    } catch (e: any) { setMessage({ type: 'error', text: e.message }); }
    finally { setCreatingOffer(null); }
  };

  const toggleActive = async (customCodeId: string, active: boolean) => {
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/admin/appstore/custom-codes/${customCodeId}/set-active`, {
        method: 'POST', headers, body: JSON.stringify({ active }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao atualizar');
      loadOverview(selectedSub);
    } catch (e: any) { setMessage({ type: 'error', text: e.message }); }
  };

  const copyLink = (code: string) => {
    navigator.clipboard?.writeText(redeemLink(code));
    setMessage({ type: 'success', text: `Link do ${code} copiado.` });
  };

  const subLabel = (s: Subscription) => FRIENDLY_NAME[s.productId] || s.name;

  useEffect(() => { checkStatus(); }, []);

  if (configured === null) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress aria-label="Carregando" /></Box>;
  if (configured === false) return <Alert severity="warning">Integração com a App Store Connect ainda não está configurada no servidor.</Alert>;

  return (
    <Box>
      {/* Cabeçalho */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ color: theme.colors.text.primary, fontWeight: 800, letterSpacing: -0.5 }}>
            Cupons Apple
          </Typography>
          <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mt: 0.5, maxWidth: 640 }}>
            Um <b>cupom</b> é o código que o cliente digita pra ganhar desconto. Cada cupom vive dentro de uma <b>oferta</b> (o desconto em si). Pra dar um desconto novo: crie a oferta e depois o cupom dentro dela.
          </Typography>
        </Box>
        <Tooltip title="Recarregar">
          <IconButton onClick={() => selectedSub && loadOverview(selectedSub)} size="small" aria-label="Recarregar ofertas"
            sx={{ border: `1px solid ${theme.colors.border}` }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3, wordBreak: 'break-word', borderRadius: theme.radii.medium }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Barra: assinatura + preço + ações */}
      <Card sx={{ ...surface, p: 2.5, mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ sm: 'center' }}>
            <TextField
              select label="Assinatura" value={selectedSub} size="small"
              onChange={e => { setSelectedSub(e.target.value); setTiers([]); loadOverview(e.target.value); }}
              sx={{ minWidth: 200 }}
            >
              {subscriptions.map(s => <MenuItem key={s.id} value={s.id}>{subLabel(s)}</MenuItem>)}
            </TextField>
            <Box>
              <Typography variant="caption" sx={{ color: theme.colors.text.secondary, display: 'block', lineHeight: 1 }}>
                Preço atual
              </Typography>
              <Typography variant="h6" sx={{ color: theme.colors.text.primary, fontWeight: 800 }}>
                R$ {formatBRL(currentPrice)}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined" startIcon={<EditIcon />}
              onClick={() => { loadTiers(); setPriceDialog(true); }}
              sx={{ borderColor: theme.colors.border, color: theme.colors.text.primary }}
            >
              Editar preço
            </Button>
            <Button
              variant="contained" startIcon={<OfferIcon />}
              onClick={() => { loadTiers(); setOfferDialog(true); }}
              sx={{ backgroundColor: theme.colors.primary }}
            >
              Nova oferta
            </Button>
            <Tooltip title="Criar um plano/produto novo (exige revisão da Apple)">
              <Button
                variant="text" startIcon={<AddBoxIcon />}
                onClick={() => { loadTiers(); loadGroups(); setProductDialog(true); }}
                sx={{ color: theme.colors.text.secondary }}
              >
                Novo plano
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
      </Card>

      {/* Lista de ofertas */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress aria-label="Carregando" /></Box>
      ) : offers.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: theme.radii.medium }}>
          Nenhuma oferta nesta assinatura. Crie uma em "Nova oferta".
        </Alert>
      ) : (
        <Stack spacing={3}>
          {offers.map(offer => (
            <Card key={offer.id} sx={{ ...surface, overflow: 'hidden' }}>
              <Box sx={{ px: 3, py: 2.5, backgroundColor: theme.colors.accent + '55', borderBottom: `1px solid ${theme.colors.border}` }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Box>
                    <Typography variant="h6" sx={{ color: theme.colors.text.primary, fontWeight: 800, lineHeight: 1.2 }}>
                      {offer.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                      {offer.duration === 'ONE_YEAR' ? '1 ano' : offer.duration} · Elegíveis: {offer.eligibilities.map(e => ELIGIBILITY_LABEL[e] || e).join(', ') || '-'}
                    </Typography>
                  </Box>
                  <Chip size="small" label={offer.active ? 'Ativa' : 'Inativa'} color={offer.active ? 'success' : 'default'} />
                </Stack>
              </Box>

              <Box sx={{ px: 3, py: 2.5 }}>
                {offer.customCodes.length === 0 ? (
                  <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 2.5 }}>
                    Nenhum código criado nesta oferta ainda.
                  </Typography>
                ) : (
                  <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                    {offer.customCodes.map(c => (
                      <Box key={c.id} sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
                        px: 2, py: 1.5, borderRadius: theme.radii.medium, backgroundColor: theme.colors.card,
                        border: `1px solid ${theme.colors.border}`,
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Typography sx={{ color: theme.colors.text.primary, fontWeight: 700, fontFamily: 'monospace', fontSize: '1.05rem', letterSpacing: 1 }}>
                            {c.customCode}
                          </Typography>
                          <Chip size="small" label={c.active ? 'Ativo' : 'Inativo'} color={c.active ? 'success' : 'default'} variant={c.active ? 'filled' : 'outlined'} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                            Limite {c.numberOfCodes ?? '-'}{c.expirationDate ? ` · exp ${new Date(c.expirationDate).toLocaleDateString('pt-BR')}` : ''}
                          </Typography>
                          <Tooltip title="Copiar link de resgate">
                            <IconButton size="small" onClick={() => copyLink(c.customCode || '')}><CopyIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Button size="small" onClick={() => toggleActive(c.id, !c.active)}>{c.active ? 'Desativar' : 'Ativar'}</Button>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}

                <Box sx={{ p: 2, borderRadius: theme.radii.medium, backgroundColor: theme.colors.card, border: `1px dashed ${theme.colors.border}` }}>
                  <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600, display: 'block', mb: 1 }}>
                    Criar cupom nesta oferta ({offer.name})
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <TextField
                      label="Código do cupom" placeholder="EX: PROMO20" value={newCodeByOffer[offer.id] || ''}
                      onChange={e => setNewCodeByOffer({ ...newCodeByOffer, [offer.id]: e.target.value.toUpperCase() })}
                      size="small" sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Limite" value={limitByOffer[offer.id] ?? '1000'}
                      onChange={e => setLimitByOffer({ ...limitByOffer, [offer.id]: e.target.value.replace(/\D/g, '') })}
                      size="small" sx={{ width: 130 }}
                      helperText="mín. 500"
                    />
                    <Button
                      variant="contained" startIcon={<AddIcon />} onClick={() => createCode(offer.id)}
                      disabled={creatingOffer === offer.id}
                      sx={{ backgroundColor: theme.colors.primary, minWidth: 130 }}
                    >
                      {creatingOffer === offer.id ? <CircularProgress aria-label="Carregando" size={18} sx={{ color: '#fff' }} /> : 'Criar cupom'}
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Card>
          ))}
        </Stack>
      )}

      <CreateOfferDialog
        open={offerDialog} onClose={() => setOfferDialog(false)} theme={theme}
        tiers={tiers} currentPrice={currentPrice} submitting={submitting}
        onSubmit={async (payload: any) => {
          setSubmitting(true); setMessage(null);
          try {
            const res = await fetch(`${API_URL}/admin/appstore/offers`, {
              method: 'POST', headers, body: JSON.stringify({ subscriptionId: selectedSub, ...payload }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Erro ao criar oferta');
            setMessage({ type: 'success', text: `Oferta "${payload.name}" criada.` });
            setOfferDialog(false); loadOverview(selectedSub);
          } catch (e: any) { setMessage({ type: 'error', text: e.message }); }
          finally { setSubmitting(false); }
        }}
      />

      <EditPriceDialog
        open={priceDialog} onClose={() => setPriceDialog(false)} theme={theme}
        tiers={tiers} currentPrice={currentPrice} submitting={submitting}
        onSubmit={async (payload: any) => {
          setSubmitting(true); setMessage(null);
          try {
            const res = await fetch(`${API_URL}/admin/appstore/price-change`, {
              method: 'POST', headers, body: JSON.stringify({ subscriptionId: selectedSub, ...payload }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Erro ao mudar preço');
            setMessage({ type: 'success', text: 'Mudança de preço agendada.' });
            setPriceDialog(false); loadOverview(selectedSub);
          } catch (e: any) { setMessage({ type: 'error', text: e.message }); }
          finally { setSubmitting(false); }
        }}
      />

      <CreateProductDialog
        open={productDialog} onClose={() => setProductDialog(false)} theme={theme}
        groups={groups} tiers={tiers} submitting={submitting}
        onSubmit={async (payload: any) => {
          setSubmitting(true); setMessage(null);
          try {
            const res = await fetch(`${API_URL}/admin/appstore/products`, {
              method: 'POST', headers, body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Erro ao criar plano');
            const warn = data.steps && (data.steps.localization !== 'ok' || (payload.pricePointId && data.steps.price !== 'ok'));
            setMessage({
              type: warn ? 'info' : 'success',
              text: warn
                ? `Plano criado (id ${data.subscriptionId}). Confira localização/preço no painel: ${JSON.stringify(data.steps)}`
                : `Plano "${payload.name}" criado. Requer revisão da Apple antes de ficar disponível.`,
            });
            setProductDialog(false);
          } catch (e: any) { setMessage({ type: 'error', text: e.message }); }
          finally { setSubmitting(false); }
        }}
      />
    </Box>
  );
};

// ---- Dialog: criar plano/produto ----
const CreateProductDialog: React.FC<any> = ({ open, onClose, theme, groups, tiers, submitting, onSubmit }) => {
  const [groupId, setGroupId] = useState('');
  const [name, setName] = useState('');
  const [productId, setProductId] = useState('');
  const [period, setPeriod] = useState('ONE_MONTH');
  const [tier, setTier] = useState<PriceTier | null>(null);

  return (
    <Dialog aria-labelledby="adminappleoffercodes-dlg1-title" open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle id="adminappleoffercodes-dlg1-title" sx={{ fontWeight: 700 }}>Novo plano</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2.5 }}>
          Cria uma assinatura nova na App Store. Depois de criada, ela precisa passar por <b>revisão da Apple</b> antes de ficar disponível pros clientes.
        </Alert>
        <Stack spacing={2.5}>
          <TextField select label="Grupo" value={groupId} onChange={e => setGroupId(e.target.value)} size="small" fullWidth>
            {groups.map((g: any) => <MenuItem key={g.id} value={g.id}>{g.referenceName}</MenuItem>)}
          </TextField>
          <TextField label="Nome de referência (interno)" value={name} onChange={e => setName(e.target.value)} size="small" fullWidth />
          <TextField label="Product ID (ex: br.com.hostly.pro.weekly)" value={productId} onChange={e => setProductId(e.target.value)} size="small" fullWidth />
          <TextField select label="Período" value={period} onChange={e => setPeriod(e.target.value)} size="small" fullWidth>
            <MenuItem value="ONE_WEEK">Semanal</MenuItem>
            <MenuItem value="ONE_MONTH">Mensal</MenuItem>
            <MenuItem value="TWO_MONTHS">Bimestral</MenuItem>
            <MenuItem value="THREE_MONTHS">Trimestral</MenuItem>
            <MenuItem value="SIX_MONTHS">Semestral</MenuItem>
            <MenuItem value="ONE_YEAR">Anual</MenuItem>
          </TextField>
          <Autocomplete
            options={tiers} value={tier} onChange={(_, v) => setTier(v)}
            getOptionLabel={(o: PriceTier) => `R$ ${formatBRL(o.customerPrice)}`}
            isOptionEqualToValue={(a: PriceTier, b: PriceTier) => a.pricePointId === b.pricePointId}
            renderInput={(params) => <TextField {...params} label="Preço base (opcional)" size="small" />}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained" disabled={submitting || !groupId || !name.trim() || !productId.trim()}
          onClick={() => onSubmit({ groupId, name: name.trim(), productId: productId.trim(), subscriptionPeriod: period, localizedName: name.trim(), pricePointId: tier?.pricePointId })}
          sx={{ backgroundColor: theme.colors.primary }}
        >
          {submitting ? <CircularProgress aria-label="Carregando" size={20} sx={{ color: '#fff' }} /> : 'Criar plano'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---- Dialog: criar oferta ----
const CreateOfferDialog: React.FC<any> = ({ open, onClose, theme, tiers, currentPrice, submitting, onSubmit }) => {
  const [name, setName] = useState('');
  const [tier, setTier] = useState<PriceTier | null>(null);
  const [elig, setElig] = useState<string[]>(['NEW', 'EXISTING', 'EXPIRED']);

  const toggleElig = (e: string) => setElig(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  const discountPct = tier && currentPrice ? Math.round((1 - parseFloat(tier.customerPrice) / parseFloat(currentPrice)) * 100) : null;

  return (
    <Dialog aria-labelledby="adminappleoffercodes-dlg2-title" open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle id="adminappleoffercodes-dlg2-title" sx={{ fontWeight: 700 }}>Nova oferta de desconto</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="Nome da oferta (interno)" value={name} onChange={e => setName(e.target.value.toUpperCase())} fullWidth size="small" />
          <Autocomplete
            options={tiers} value={tier} onChange={(_, v) => setTier(v)}
            getOptionLabel={(o: PriceTier) => `R$ ${formatBRL(o.customerPrice)}`}
            isOptionEqualToValue={(a: PriceTier, b: PriceTier) => a.pricePointId === b.pricePointId}
            renderInput={(params) => <TextField {...params} label="Preço promocional (1º ano)" size="small" />}
            renderOption={(props, o: PriceTier) => (
              <li {...props} key={o.pricePointId}>R$ {formatBRL(o.customerPrice)} <span style={{ color: theme.colors.text.secondary, marginLeft: 8, fontSize: '0.8em' }}>(recebe R$ {formatBRL(o.proceeds)})</span></li>
            )}
          />
          {discountPct != null && discountPct > 0 && (
            <Chip label={`${discountPct}% de desconto sobre R$ ${formatBRL(currentPrice)}`} sx={{ alignSelf: 'flex-start', backgroundColor: theme.colors.primary, color: '#fff' }} />
          )}
          <Box>
            <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600 }}>Clientes elegíveis</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {['NEW', 'EXISTING', 'EXPIRED'].map(e => (
                <Chip key={e} label={ELIGIBILITY_LABEL[e]} onClick={() => toggleElig(e)}
                  color={elig.includes(e) ? 'primary' : 'default'} variant={elig.includes(e) ? 'filled' : 'outlined'} />
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained" disabled={submitting || !name.trim() || !tier || elig.length === 0}
          onClick={() => onSubmit({ name: name.trim(), pricePointId: tier!.pricePointId, customerEligibilities: elig, offerMode: 'PAY_AS_YOU_GO', duration: 'ONE_YEAR', numberOfPeriods: 1 })}
          sx={{ backgroundColor: theme.colors.primary }}
        >
          {submitting ? <CircularProgress aria-label="Carregando" size={20} sx={{ color: '#fff' }} /> : 'Criar oferta'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---- Dialog: editar preço ----
const EditPriceDialog: React.FC<any> = ({ open, onClose, theme, tiers, currentPrice, submitting, onSubmit }) => {
  const [tier, setTier] = useState<PriceTier | null>(null);
  const [preserve, setPreserve] = useState(true);

  return (
    <Dialog aria-labelledby="adminappleoffercodes-dlg3-title" open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle id="adminappleoffercodes-dlg3-title" sx={{ fontWeight: 700 }}>Editar preço do plano</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          Muda o preço de venda do plano na App Store. Preço atual: <b>R$ {formatBRL(currentPrice)}</b>.
        </Alert>
        <Stack spacing={2.5}>
          <Autocomplete
            options={tiers} value={tier} onChange={(_, v) => setTier(v)}
            getOptionLabel={(o: PriceTier) => `R$ ${formatBRL(o.customerPrice)}`}
            isOptionEqualToValue={(a: PriceTier, b: PriceTier) => a.pricePointId === b.pricePointId}
            renderInput={(params) => <TextField {...params} label="Novo preço" size="small" />}
            renderOption={(props, o: PriceTier) => (
              <li {...props} key={o.pricePointId}>R$ {formatBRL(o.customerPrice)} <span style={{ color: theme.colors.text.secondary, marginLeft: 8, fontSize: '0.8em' }}>(recebe R$ {formatBRL(o.proceeds)})</span></li>
            )}
          />
          <FormControlLabel
            control={<Switch checked={preserve} onChange={e => setPreserve(e.target.checked)} />}
            label="Manter preço atual para quem já assina (só novos pagam o novo preço)"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained" color="warning" disabled={submitting || !tier}
          onClick={() => onSubmit({ pricePointId: tier!.pricePointId, preserveExisting: preserve })}
        >
          {submitting ? <CircularProgress aria-label="Carregando" size={20} sx={{ color: '#fff' }} /> : 'Confirmar mudança'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
